import { CoreInterfaces, DTOs } from "src/core/Models";
import * as Constants from "./../../core/Constants";
import { findQuestionInService } from "./../shared";
import { calculateEstimatedTimeInMinutes } from "./../task-utils";
import { ceil, round } from "./../utils";

// calculation
export function getServiceVariable(
  serviceDTO: DTOs.ServiceDTO,
  variableName: Constants.CalculationVariable
): any {
  if (
    serviceDTO.data.calculationVariables &&
    serviceDTO.data.calculationVariables[variableName]
  ) {
    const question = serviceDTO.data.questions.find(
      (questionDTO) =>
        questionDTO.data.code ===
        serviceDTO.data.calculationVariables[variableName]
    );
    if (question) {
      return question.data.userValue;
    }
  }
  return null;
}

export function getTaskCalculationVariable(
  serviceDTO: DTOs.ServiceDTO,
  taskDTO: DTOs.TaskDTO
): any {
  if (taskDTO.data.calculationQuestion) {
    const question = findQuestionInService(
      serviceDTO,
      taskDTO.data.calculationQuestion
    );
    if (question) {
      return question.data.userValue;
    }
  }
  return null;
}

export function getTaskCalculationMultiplicator(
  serviceDTO: DTOs.ServiceDTO,
  taskDTO: DTOs.TaskDTO
): any {
  if (taskDTO.data.calculationTask) {
    const taskRelated = serviceDTO.data.tasks.find(
      (task) => task.data.code === taskDTO.data.calculationTask
    );

    if (taskRelated) {
      return (
        Constants.FrequencyValueMap[taskRelated.data.adjustments.frequency] ??
        0 * (taskRelated.data.adjustments.estimatedTimePerUnit ?? 0)
      );
    }
  }
  return null;
}

export function getServiceStaffLevelUsageMap(
  serviceDTO: DTOs.ServiceDTO,
  globalState: CoreInterfaces.AppState,
  taskGroups: Array<Constants.TaskGroup> = []
): CoreInterfaces.ServiceStaffLevelUsageMap<number> {
  const staffLevelUsageMap: CoreInterfaces.ServiceStaffLevelUsageMap<number> =
    {};

  const tasks = serviceDTO?.data?.tasks ?? [];
  const usedTasks = tasks.filter((taskDTO) => {
    return (
      taskDTO.state.isVisible &&
      taskDTO.state.isActive &&
      taskGroups.includes(taskDTO.data.serviceTaskGroup)
    );
  });

  const usedTasksUpdated =
    assignStaffLevelsBasedOnSelectedServices(
      serviceDTO,
      globalState,
      usedTasks
    ) ?? [];

  usedTasksUpdated.forEach((taskDTO) => {
    const staffLevel =
      taskDTO.data.adjustments.staffLevel ??
      (taskDTO.data.staffLevel as Constants.StaffLevel);
    const taskGroup = taskDTO.data.serviceTaskGroup;

    if (staffLevel && taskGroup) {
      staffLevelUsageMap[staffLevel] = staffLevelUsageMap[staffLevel] || {};
      const totalTimeForStaffLevel =
        (staffLevelUsageMap[staffLevel][taskGroup] || 0) +
        calculateEstimatedTimeInMinutes(serviceDTO, taskDTO);
      if (totalTimeForStaffLevel !== 0) {
        staffLevelUsageMap[staffLevel][taskGroup] = totalTimeForStaffLevel;
      }
    }
  });

  // cleanup 0 values
  for (const staffLevel in staffLevelUsageMap) {
    for (const taskGroup in staffLevelUsageMap[
      staffLevel as Constants.StaffLevel
    ]) {
      if (
        staffLevelUsageMap[staffLevel as Constants.StaffLevel][taskGroup] === 0
      ) {
        delete staffLevelUsageMap[staffLevel as Constants.StaffLevel][
          taskGroup
        ];
      }
    }
    if (
      Object.keys(staffLevelUsageMap[staffLevel as Constants.StaffLevel])
        .length === 0
    ) {
      delete staffLevelUsageMap[staffLevel as Constants.StaffLevel];
    }
  }

  return staffLevelUsageMap;
}

function assignStaffLevelsBasedOnSelectedServices(
  serviceDTO: DTOs.ServiceDTO,
  globalState: CoreInterfaces.AppState,
  usedTasks: DTOs.TaskDTO[]
) {
  let isOnlyPayrollSelected = false;
  if (
    serviceDTO.data.code ===
    Constants.ServiceCode.YearlyInternalDocumentationAndFormalities
  ) {
    const selectedServices = globalState.services.filter(
      (s) => s.state.isSelected
    );

    isOnlyPayrollSelected =
      selectedServices.length === 5 &&
      selectedServices[3].data.code ===
        Constants.ServiceCode.YearlyInternalDocumentationAndFormalities;
  }

  usedTasks.forEach((taskDTO) => {
    switch (taskDTO.data.code) {
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1005:
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1008:
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1010:
        taskDTO.data.staffLevel = isOnlyPayrollSelected
          ? Constants.StaffLevel.ConsultantPayroll
          : Constants.StaffLevel.ConsultantAccounting;
        break;
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1007:
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1013:
        taskDTO.data.staffLevel = isOnlyPayrollSelected
          ? Constants.StaffLevel.ManagerPayroll
          : Constants.StaffLevel.ManagerAccounting;
        break;
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1006:
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1009:
      case Constants.YearlyInternalDocumentationAndFormalitiesTasks.T1011:
        taskDTO.data.staffLevel = isOnlyPayrollSelected
          ? Constants.StaffLevel.AssistantManagerPayroll
          : Constants.StaffLevel.AssistantManagerAccounting;
        break;

      default:
        break;
    }
  });

  return usedTasks;
}

/**
 *
 * @param appState
 * @param serviceDTO
 * @param taskGroups
 * @returns an object of type CoreInterfaces.ServiceMonthlyStats
 * @description This function returns an object of type CoreInterfaces.ServiceMonthlyStats. This contains the calculated data for a service ( like: additionalCosts, softwareServiceCosts, workTime, totalPrice) that is used to be displayed in various parts of the application
 * This uses various layers of data ( staffLevel allocation, additionalCosts) in order to calculate the workTime, totalPrice, additionalCosts
 */
export function getServiceStats(
  appState: CoreInterfaces.AppState,
  serviceDTO: DTOs.ServiceDTO,
  taskGroups?: Constants.TaskGroup[]
): CoreInterfaces.ServiceMonthlyStats {
  let mapTaskGroupsToTaskCodes = serviceDTO.data.serviceTaskGroups.map(
    (taskgroup) => taskgroup.Code
  );
  if (!!taskGroups) {
    mapTaskGroupsToTaskCodes = taskGroups;
  }
  const staffLevelUsageMap = getServiceStaffLevelUsageMap(
    serviceDTO,
    appState,
    mapTaskGroupsToTaskCodes
  );

  const serviceTotalWorkCostVariable = getServiceVariable(
    serviceDTO,
    Constants.CalculationVariable.TotalWorkCost
  );
  const totalWorkCostVariable = Number(serviceTotalWorkCostVariable);

  const recurringMultiplier =
    Constants.RecurringTypeMultiplierMap[serviceDTO.data.recurringType] ?? 12;
  let totalWorkCost = 0;
  let totalWorkTimeInMinutesPerMonth = 0;
  let workCost = 0;
  const adjustments = serviceDTO.data.staffingAdjustments.cost ?? 0;
  const rateCartConfiguration =
    appState.currentConfiguration?.applicationConfiguration
      ?.rateCartConfiguration;

  for (const k in staffLevelUsageMap) {
    const staffLevelCode = k as Constants.StaffLevel;
    const rateCart = rateCartConfiguration.RateCarts.find(
      (eachRateCart) => eachRateCart.StaffLevelCode === staffLevelCode
    );
    if (!!rateCart) {
      const workMinutes = staffLevelUsageMap[staffLevelCode];
      for (const taskGroupWorkMinutes in workMinutes) {
        const workHours = ceil(workMinutes[taskGroupWorkMinutes]) / 60;
        workCost = workHours * rateCart.RateOnHour;
        totalWorkTimeInMinutesPerMonth += workMinutes[taskGroupWorkMinutes];
        totalWorkCost += workCost;
      }
    }
  }
  const additionalCostsTotal = getServiceAdditionalCostsTotal(serviceDTO);

  let additionalCostsAdjustment = 0;
  serviceDTO.data.additionalCosts.forEach((additionalCost) => {
    if (additionalCost.state.isVisible) {
      if (!!additionalCost.data.adjustments.Cost) {
        additionalCostsAdjustment += additionalCost.data.adjustments.Cost;
      } else {
        additionalCostsAdjustment += additionalCost.data.cost;
      }
    }
  });

  const extendedPriceListPanelCost =
    serviceDTO.data.staffingAdjustments.extendedPriceListPanelCost || 0;
  const totalPrice =
    totalWorkCost +
    totalWorkCostVariable +
    additionalCostsTotal +
    (adjustments || 0);
  const totalPricePerMonth = ceil(totalPrice);
  const totalPricePerYear = recurringMultiplier * totalPricePerMonth;
  const totalWorkTimeInMinutesPerYear =
    recurringMultiplier * round(totalWorkTimeInMinutesPerMonth);
  const object: CoreInterfaces.ServiceMonthlyStats = {
    additionalCostsAdjustment: additionalCostsAdjustment,
    workTimeInMinutesPerMonth: totalWorkTimeInMinutesPerMonth,
    workTimePricePerMonth: totalWorkCost + adjustments,
    softwareServicesCostPerMonth: additionalCostsTotal,
    totalPricePerMonth,
    totalPricePerYear,
    workTimeInMinutesPerYear: totalWorkTimeInMinutesPerYear,
    extendedPriceListPanelCostAdjustment: extendedPriceListPanelCost,
  };
  return object;
}

export function getServiceAdditionalCostsTotal(
  serviceDTO: DTOs.ServiceDTO
): number {
  let additionalCostsTotal = 0;
  const visibleAdditionalCostDTOs = serviceDTO.data.additionalCosts.filter(
    (eachAdditionalCostDTO) => eachAdditionalCostDTO.state.isVisible
  );
  for (const additionalCostDTO of visibleAdditionalCostDTOs) {
    let usedValue = null;
    if (!!additionalCostDTO.data.numberOfUnits) {
      usedValue = additionalCostDTO.data.numberOfUnits;
    } else {
      if (!!additionalCostDTO.data.valueQuestionCode) {
        const usedQuestion = findQuestionInService(
          serviceDTO,
          additionalCostDTO.data.valueQuestionCode
        );
        if (!!usedQuestion && !!usedQuestion.data.userValue) {
          usedValue = usedQuestion.data.userValue;
        }
      }
    }
    const rowTotal =
      Number(usedValue) * additionalCostDTO.data.cost +
      (additionalCostDTO.data.fixedCost || 0);
    additionalCostsTotal += rowTotal;
  }
  return additionalCostsTotal;
}

//get multiplicator
export function getFrequencyMultiplicator(
  taskDTO: DTOs.TaskDTO,
  serviceDTO: DTOs.ServiceDTO
) {
  let multiplicator = 0;
  switch (taskDTO.data.calculationMethod) {
    case Constants.TaskCalculationMethod.AmountXTimeXPricePerHour: {
      if (
        !!taskDTO.data.calculationQuestionOverride &&
        taskDTO.data.calculationQuestionOverride.state.isShown
      ) {
        multiplicator = taskDTO.data.calculationQuestionOverride.data.userValue
          ? parseFloat(
              taskDTO.data.calculationQuestionOverride.data.userValue as string
            )
          : 0;
      } else {
        if (!!taskDTO.data.calculationQuestion) {
          multiplicator = getTaskCalculationVariable(serviceDTO, taskDTO) ?? 0;
        } else {
          multiplicator =
            getServiceVariable(
              serviceDTO,
              Constants.CalculationVariable.Amount
            ) ?? 0;
        }
      }
      break;
    }
    case Constants.TaskCalculationMethod.FrequencyXTimeXPricePerHour:
    case Constants.TaskCalculationMethod.FrequencyXAmountXTime: {
      const frequency = !!taskDTO.data.adjustments.frequency
        ? taskDTO.data.adjustments.frequency
        : taskDTO.data.frequency;
      if (taskDTO.data.calculationTask) {
        multiplicator =
          getTaskCalculationMultiplicator(serviceDTO, taskDTO) ?? 0;
      } else {
        if (!!frequency) {
          const frequencyValue = Constants.FrequencyValueMap[frequency];
          if (
            taskDTO.data.type === Constants.TaskType.ManuallyAdded &&
            (serviceDTO.data.recurringType as string) === frequency
          ) {
            multiplicator = 1;
          } else {
            multiplicator = frequencyValue !== undefined ? frequencyValue : 0;
          }
        }
      }
      break;
    }
    case Constants.TaskCalculationMethod.TimeXPricePerHour: {
      if (!!taskDTO.data.calculationQuestion) {
        multiplicator = getTaskCalculationVariable(serviceDTO, taskDTO) ?? 1;
      } else {
        multiplicator = 1;
      }
      break;
    }
  }

  return multiplicator;
}

export function mapServiceCodesToServicesDTOs(
  globalState: CoreInterfaces.AppState,
  serviceCodes: Array<Constants.ServiceCode>
): {
  [key in Constants.ServiceCode]?: DTOs.ServiceDTO;
} {
  const object: { [key in Constants.ServiceCode]?: DTOs.ServiceDTO } = {};
  for (const serviceDTO of globalState.services) {
    if (serviceCodes.includes(serviceDTO.data.code)) {
      object[serviceDTO.data.code] = serviceDTO;
    }
  }
  return object;
}

export function sanitizeWorkMinutes(workMinutes: number): number {
  return isNaN(workMinutes) ? 0 : workMinutes;
}

export function calculateTaskGroupSum(
  workMinutes: CoreInterfaces.ServiceStaffLevelUsageMap<number>,
  taskGroups: Array<string>
): number {
  let totalSum = 0;

  taskGroups.forEach((taskGroup) => {
    for (const staffLevel in workMinutes) {
      const staffLevelMinutes = workMinutes[staffLevel as Constants.StaffLevel];
      if (staffLevelMinutes && staffLevelMinutes[taskGroup]) {
        const taskGroupMinutes = staffLevelMinutes[taskGroup];
        totalSum += taskGroupMinutes;
      }
    }
  });
  return totalSum;
}
